import { Form, Modal, Spin, Typography, Checkbox } from 'antd';
import Button from '../../UI/Button/Button';
import React from 'react';
import Input from '../../UI/Input/Input';
import Select from '../../UI/Select/Select';
import DatePicker from '../../UI/DatePicker/DatePicker';
import PasswordInput from '../../UI/Input/PasswordInput';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import parse from 'html-react-parser';
import styles from './Signup.module.scss';
import countries from '../../../constants/countries';

const SignUpModal = ({
  open,
  onClose,
  onSubmit,
  onSignIn,
  onChange,
  isValid,
  form,
  loading
}) => {
  const { t } = useTranslation();

  return (
      <Modal
        open={open}
        footer={null}
        onCancel={onClose}
        className={styles.modalFixedHeight}
      >
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1000
          }}
        >
          <Spin
            indicator={<LoadingOutlined spin />}
            style={{ height: 'fit-content' }}
          />
        </div>
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          alignItems: 'center',
          marginTop: 10,
          width: '100%'
        }}
      >
        <h2 style={{ textAlign: 'center' }}>{t('modals.signUp.title')}</h2>
        <Form
          form={form}
          layout='vertical'
          onFinish={onSubmit}
          style={{ marginTop: 40, width: '100%' }}
          onFieldsChange={onChange}
        >
          <Form.Item
            name='email'
            rules={[
              {
                required: true,
                message: t('schema.required')
              },
              () => ({
                validator(_, value) {
                  if (value && !/^\S+@\S+\.\S+$/.test(value)) {
                    return Promise.reject(t('schema.email.invalid'));
                  }
                  return Promise.resolve();
                }
              })
            ]}
          >
            <Input placeholder={t('input.email')} />
          </Form.Item>
          <div style={{ display: 'flex', gap: 40 }}>
            <Form.Item
              style={{ width: '100%' }}
              name='firstname'
              rules={[
                {
                  required: true,
                  message: t('schema.required')
                },
                { min: 2, message: t('schema.name.min') },
                { max: 100, message: t('schema.name.max') },
                {
                  pattern: /^[A-Za-z ]+$/i,
                  message: t('schema.lettersAndSpaces')
                }
              ]}
            >
              <Input placeholder={t('input.firstname')} />
            </Form.Item>
            <Form.Item
              style={{ width: '100%' }}
              name='lastname'
              rules={[
                {
                  required: true,
                  message: t('schema.required')
                },
                { min: 2, message: t('schema.name.min') },
                { max: 100, message: t('schema.name.max') },
                {
                  pattern: /^[A-Za-z ]+$/i,
                  message: t('schema.lettersAndSpaces')
                }
              ]}
            >
              <Input placeholder={t('input.lastname')} />
            </Form.Item>
          </div>
          <Form.Item
            name='dateOfBirth'
            rules={[
              {
                required: true,
                message: t('schema.required')
              },
              () => ({
                validator(_, value) {
                  const today = new Date();
                  const userValue = new Date(value);
                  let age = today.getFullYear() - userValue.getFullYear();
                  const m = today.getMonth() - userValue.getMonth();
                  if (
                    m < 0 ||
                    (m === 0 && today.getDate() < userValue.getDate())
                  ) {
                    age--;
                  }

                  if (age < 13) {
                    return Promise.reject(t('schema.dateOfBirth.age'));
                  }
                  return Promise.resolve();
                }
              })
            ]}
          >
            <DatePicker
              style={{ width: '100%' }}
              inputReadOnly={true}
              placeholder={t('input.dateOfBirth')}
            />
          </Form.Item>

          <div style={{ display: 'flex', gap: 40, alignItems: 'flex-start' }}>
            <Form.Item
              name='password'
              rules={[
                { required: true, message: t('schema.required') },
                () => ({
                  validator(_, value) {
                    if (
                      value &&
                      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@"':;#$%^&*?,()\\/!-_+=[\]]{8,}$/.test(
                        value
                      )
                    ) {
                      return Promise.reject(t('schema.password.invalid'));
                    }
                    return Promise.resolve();
                  }
                })
              ]}
              style={{ position: 'relative', width: '100%' }}
            >
              <div style={{ position: 'relative' }}>
                <PasswordInput
                  placeholder={t('input.password')}
                  style={{ width: '100%' }}
                />
              </div>
            </Form.Item>

            <Form.Item
              dependencies={['password']}
              name='confirmPassword'
              rules={[
                { required: true },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('schema.password.match')));
                  }
                })
              ]}
              style={{ position: 'relative', width: '100%' }}
            >
              <div style={{ position: 'relative' }}>
                <PasswordInput
                  placeholder={t('input.confirmPassword')}
                  style={{ width: '100%' }}
                />
              </div>
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: 40 }}>
            <Form.Item name="country" style={{ width: '100%' }}>
              <Select
                placeholder={t('input.optional.country')}
                showSearch
                allowClear
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
                options={Object.entries(countries).map(([code, name]) => ({
                  label: name,
                  value: code
                }))}
              />
            </Form.Item>
            <Form.Item
              name='zip'
              style={{ width: '100%' }}
              rules={[
                () => ({
                  validator(_, value) {
                    return !value || value.trim() !== ''
                      ? Promise.resolve()
                      : Promise.reject(t('schema.requiredIfFilled'));
                  }
                })
              ]}
            >
              <Input placeholder={t('input.optional.zip')} />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: 40 }}>
            <Form.Item
              name='city'
              style={{ width: '100%' }}
              rules={[
                () => ({
                  validator(_, value) {
                    return !value || value.trim() !== ''
                      ? Promise.resolve()
                      : Promise.reject(t('schema.requiredIfFilled'));
                  }
                })
              ]}
            >
              <Input placeholder={t('input.optional.city')} />
            </Form.Item>

            <Form.Item
              name='street'
              style={{ width: '100%' }}
              rules={[
                () => ({
                  validator(_, value) {
                    return !value || value.trim() !== ''
                      ? Promise.resolve()
                      : Promise.reject(t('schema.requiredIfFilled'));
                  }
                })
              ]}
            >
              <Input placeholder={t('input.optional.street')} />
            </Form.Item>
          </div>

          <div style={{ display: 'flex', gap: 40 }}>
            <Form.Item
              name='house'
              style={{ width: '100%' }}
              rules={[
                () => ({
                  validator(_, value) {
                    return !value || value.trim() !== ''
                      ? Promise.resolve()
                      : Promise.reject(t('schema.requiredIfFilled'));
                  }
                })
              ]}
            >
              <Input placeholder={t('input.optional.house')} />
            </Form.Item>

            <Form.Item
              name='flat'
              style={{ width: '100%' }}
            >
              <Input placeholder={t('input.flat')} />
            </Form.Item>
          </div>


          <Form.Item
            name='agreement'
            valuePropName='checked'
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(new Error(t('schema.required')))
              }
            ]}
          >
            <Checkbox
              style={{
                fontSize: 16,
                fontWeight: 400,
                alignItems: 'center',
                lineHeight: '21.09px'
              }}
            >
              {parse(t('input.agreement'))}
            </Checkbox>
          </Form.Item>
          <Form.Item name='marketConsent' valuePropName='checked'>
            <Checkbox
              style={{
                fontSize: 16,
                fontWeight: 400,
                alignItems: 'center',
                lineHeight: '21.09px'
              }}
            >
              {parse(t('input.marketConsent'))}
            </Checkbox>
          </Form.Item>

          <Button
            onClick={form.submit}
            style={{ marginBottom: 20 }}
            wide
            disabled={!isValid}
          >
            {t('button.signUp')}
          </Button>
        </Form>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <Typography
            style={{ fontSize: 20, fontWeight: 400, textAlign: 'center' }}
          >
            {t('modals.signUp.haveAccount')}
          </Typography>
          <Typography
            style={{
              fontSize: 20,
              fontWeight: 400,
              textDecoration: 'underline',
              color: '#9989C7',
              cursor: 'pointer'
            }}
            onClick={onSignIn}
          >
            {t('button.signIn')}
          </Typography>
        </div>
      </div>
    </Modal>
  );
};

export default SignUpModal;
